import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PortalShell } from "@/components/Portal";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, CalendarDays, CreditCard, User, Loader2, Megaphone, Clock, Plus,
} from "lucide-react";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "children", label: "My Children", icon: Users },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "profile", label: "My Details", icon: User },
];

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export default function MemberPortal() {
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const { refresh } = useAuth();

  useEffect(() => {
    api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/portal/calendar").then((r) => setCalendar(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-primary" size={40} /></div>;

  return (
    <PortalShell title="Member Portal" tabs={TABS} active={tab} onTab={setTab}>
      {tab === "dashboard" && <Dashboard data={data} />}
      {tab === "children" && <Children data={data} />}
      {tab === "calendar" && <Calendar events={calendar} />}
      {tab === "payments" && <Payments payments={data.payments} />}
      {tab === "profile" && <Profile user={data.user} onSaved={refresh} />}
    </PortalShell>
  );
}

const Panel = ({ title, children, testid }) => (
  <div className="rounded-3xl bg-white border border-border p-6 mb-6" data-testid={testid}>
    <h2 className="font-display text-2xl text-slate-800 mb-4">{title}</h2>
    {children}
  </div>
);

function Dashboard({ data }) {
  const stats = [
    { label: "My Children", value: data.gymnasts.length, color: "bg-primary" },
    { label: "Booked Classes", value: data.bookings.length, color: "bg-secondary" },
    { label: "Upcoming Sessions", value: data.upcoming_sessions.length, color: "bg-accent text-accent-foreground" },
  ];
  return (
    <div data-testid="member-dashboard">
      <h1 className="font-display text-3xl text-primary mb-6 lg:hidden">Dashboard</h1>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {stats.map((s) => (
          <div key={s.label} className={`rounded-3xl p-5 text-white ${s.color}`}>
            <div className="text-3xl font-display">{s.value}</div>
            <div className="text-sm opacity-90">{s.label}</div>
          </div>
        ))}
      </div>

      {data.announcements.length > 0 && (
        <Panel title="Announcements" testid="member-announcements">
          {data.announcements.map((a) => (
            <div key={a.id} className="flex gap-3 py-2 border-b border-border last:border-0">
              <Megaphone className="text-primary shrink-0 mt-1" size={18} />
              <div>
                <div className="font-semibold text-slate-800">{a.title}</div>
                <div className="text-slate-600 text-sm">{a.body}</div>
              </div>
            </div>
          ))}
        </Panel>
      )}

      <Panel title="Upcoming Sessions" testid="member-upcoming">
        {data.upcoming_sessions.length === 0 ? (
          <p className="text-slate-500">No upcoming sessions scheduled.</p>
        ) : (
          <div className="space-y-2">
            {data.upcoming_sessions.map((s, i) => (
              <div key={i} className="flex items-center gap-3 rounded-2xl bg-background p-3">
                <Clock className="text-secondary" size={18} />
                <div className="flex-1">
                  <div className="font-semibold text-slate-800">{fmtDate(s.date)}</div>
                  <div className="text-sm text-slate-500">{s.school} · {s.start_time}-{s.end_time} · {s.gymnast}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Children({ data }) {
  return (
    <div data-testid="member-children">
      <div className="flex items-center justify-between mb-4 lg:mb-6">
        <h1 className="font-display text-3xl text-primary lg:hidden">My Children</h1>
        <Link to="/book-online" className="ml-auto">
          <Button data-testid="add-child-btn" className="rounded-full font-display bg-primary hover:bg-primary/90">
            <Plus size={16} className="mr-1" /> Book Another Child
          </Button>
        </Link>
      </div>
      {data.bookings.length === 0 ? (
        <Panel title="No bookings yet"><p className="text-slate-500">You have no active bookings.</p></Panel>
      ) : (
        data.bookings.map((b) => (
          <Panel key={b.id} title={b.gymnast ? `${b.gymnast.first_name} ${b.gymnast.last_name}` : "Gymnast"}>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Info label="Class" value={b.class ? `${b.class.day_name} – ${b.class.school}` : "—"} />
              <Info label="Time" value={b.class ? `${b.class.start_time} - ${b.class.end_time}` : "—"} />
              <Info label="Monthly cost" value={`£${b.monthly_cost.toFixed(2)}`} />
              <Info label="T-shirt size" value={b.gymnast?.tshirt_size || "—"} />
            </div>
            <Badge className="mt-4 bg-success/10 text-success hover:bg-success/10 rounded-full">Active</Badge>
          </Panel>
        ))
      )}
    </div>
  );
}

function Calendar({ events }) {
  const upcoming = [...events].sort((a, b) => a.date.localeCompare(b.date))
    .filter((e) => new Date(e.date) >= new Date(new Date().toDateString()));
  return (
    <div data-testid="member-calendar">
      <h1 className="font-display text-3xl text-primary mb-6 lg:hidden">Calendar</h1>
      <Panel title="Session Calendar">
        <p className="text-sm text-slate-500 mb-4">
          Only real sessions are shown. School holidays, half terms and bank holidays are excluded automatically.
        </p>
        {upcoming.length === 0 ? (
          <p className="text-slate-500">No upcoming sessions.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {upcoming.map((e, i) => (
              <div key={i} className="rounded-2xl border border-border p-4 hover:border-primary transition-colors">
                <div className="font-display text-lg text-primary">{fmtDate(e.date)}</div>
                <div className="text-slate-700 text-sm">{e.school}</div>
                <div className="text-slate-500 text-sm">{e.time}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Payments({ payments }) {
  return (
    <div data-testid="member-payments">
      <h1 className="font-display text-3xl text-primary mb-6 lg:hidden">Payments</h1>
      <Panel title="Payment History">
        {payments.length === 0 ? (
          <p className="text-slate-500">No payments yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-semibold text-slate-800">{p.type}</div>
                  <div className="text-sm text-slate-500">{fmtDate(p.date)}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg text-slate-800">£{p.amount.toFixed(2)}</div>
                  <Badge className="bg-success/10 text-success hover:bg-success/10 rounded-full capitalize">{p.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Profile({ user, onSaved }) {
  const [f, setF] = useState({
    name: user.name || "", phone: user.phone || "",
    address: user.address || "", emergency_contact: user.emergency_contact || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/portal/profile", f);
      toast.success("Details updated");
      onSaved && onSaved();
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="member-profile">
      <h1 className="font-display text-3xl text-primary mb-6 lg:hidden">My Details</h1>
      <Panel title="Contact Details">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Full Name</Label><Input className="rounded-xl mt-1" value={f.name} onChange={set("name")} data-testid="profile-name" /></div>
          <div><Label>Email (locked)</Label><Input className="rounded-xl mt-1 bg-slate-50" value={user.email} disabled /></div>
          <div><Label>Phone</Label><Input className="rounded-xl mt-1" value={f.phone} onChange={set("phone")} data-testid="profile-phone" /></div>
          <div><Label>Emergency Contact</Label><Input className="rounded-xl mt-1" value={f.emergency_contact} onChange={set("emergency_contact")} data-testid="profile-emergency" /></div>
          <div className="sm:col-span-2"><Label>Address</Label><Input className="rounded-xl mt-1" value={f.address} onChange={set("address")} data-testid="profile-address" /></div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="profile-save"
          className="mt-4 rounded-full font-display bg-primary hover:bg-primary/90 px-8">
          {saving ? <Loader2 className="animate-spin" /> : "Save Changes"}
        </Button>
      </Panel>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-slate-800 font-medium">{value}</div>
  </div>
);
